import {
  Component,
  computed,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { map, Observable, startWith } from 'rxjs';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteOrigin,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption,
} from '@angular/material/autocomplete';
import { NgForOf } from '@angular/common';
import { DatePickerType, IDateData, IDatePicker } from '../date.const';

@Component({
  selector: 'app-datepicker',
  standalone: true,
  imports: [ReactiveFormsModule, MatOption, NgForOf, MatAutocompleteTrigger, MatAutocomplete, MatAutocompleteOrigin],
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
})
export class DatepickerComponent implements OnInit, OnChanges {
  @Input() dateData: IDateData | undefined;
  @Input() validate: boolean = false;
  @Output() dateChange = new EventEmitter();

  userYears = input<number>(100);
  yearControl = new FormControl('', [Validators.required]);
  monthControl = new FormControl('');
  dayControl = new FormControl('');
  pickerData: IDateData | null = null;

  years = computed(() => {
    return Array.from({ length: this.userYears() }, (_, i) => new Date().getFullYear() - i);
  });

  months: string[] = [
    '',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  days: number[] = Array.from({ length: 31 }, (_, i) => i + 1);

  filteredYears!: Observable<number[]>;
  filteredMonths!: Observable<string[]>;
  filteredDays!: Observable<number[]>;

  selectedYear: number | undefined;
  selectedMonth: number | undefined;
  selectedDay: number | undefined;

  constructor() {
    this.filteredYears = this.yearControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterValues(value ?? '', this.years())),
    );

    this.filteredMonths = this.monthControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterValues(value ?? '', this.months)),
    );

    this.filteredDays = this.dayControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterValues(value ?? '', this.days)),
    );

    // default state of month and year control is disabled
    this.monthControl.disable();
    this.dayControl.disable();

    this.yearControl.valueChanges.subscribe((value) => {
      if (value) {
        this.monthControl.enable();
      } else {
        this.selectedDay = undefined;
        this.dayControl.setValue(null);

        this.selectedMonth = undefined;
        this.monthControl.setValue(null);

        this.monthControl.disable();
        this.dayControl.disable();
      }
    });

    this.monthControl.valueChanges.subscribe((value) => {
      if (value) {
        this.updateDays();
        this.dayControl.enable();
      } else {
        this.selectedDay = undefined;
        this.dayControl.setValue(null);
        this.dayControl.disable();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.init();

    if (changes && changes['validate'] && changes['validate'].currentValue) {
      this.yearControl.markAsTouched();
    }
  }

  ngOnInit() {
    this.init();
  }

  onBlur(type: DatePickerType) {
    if (type === DatePickerType.YEAR) {
      if (!this.yearControl.value || !this.years().includes(+this.yearControl.value)) {
        this.yearControl.setValue(null);
        this.setPickerData(null, {
          year: undefined,
          day: undefined,
          month: undefined,
        });
      }
    }

    if (this.selectedYear) {
      if (type === DatePickerType.MONTH) {
        if (!this.monthControl.value || !this.months.includes(this.monthControl.value)) {
          this.monthControl.setValue(null);

          this.setPickerData(DatePickerType.YEAR, { year: this.selectedYear, month: undefined, day: undefined });
        }
      }

      if (type === DatePickerType.DAY) {
        if (!this.dayControl.value || !this.days.includes(+this.dayControl.value)) {
          this.dayControl.setValue(null);
          this.setPickerData(DatePickerType.MONTH, {
            year: this.selectedYear,
            month: this.selectedMonth,
            day: undefined,
          });
        }
      }

      this.dateChange.emit(this.pickerData);
    }
  }

  resetAllPickers() {
    this.selectedYear = undefined;
    this.selectedMonth = undefined;
    this.selectedDay = undefined;

    this.yearControl.setValue(null);
    this.monthControl.setValue(null);
    this.dayControl.setValue(null);

    this.setPickerData(null, {
      year: undefined,
      day: undefined,
      month: undefined,
    });
  }

  init() {
    if (!this.dateData || !this.dateData.dateComponents) {
      this.resetAllPickers();
      return;
    }

    const currentDate = this.dateData.dateComponents;
    const pickerData = this.pickerData?.dateComponents;

    if (
      ((currentDate && pickerData && pickerData.year !== currentDate.year) ||
      pickerData?.month !== currentDate.month ||
      pickerData?.day !== currentDate.day
    ) || !pickerData)  {
      const year = currentDate && currentDate.year ? currentDate.year.toString() : null;
      const month = currentDate && currentDate.month ? this.months[currentDate.month] : null;

      if (this.dateData.type === DatePickerType.YEAR) {
        this.yearControl.setValue(year);
      }

      if (this.dateData.type === DatePickerType.MONTH) {
        this.yearControl.setValue(year);
        this.monthControl.setValue(month);
      }

      if (this.dateData.type === DatePickerType.DAY) {
        this.yearControl.setValue(year);
        this.monthControl.setValue(month);

        if (currentDate.day) {
          this.dayControl.setValue(currentDate.day.toString());
        }
      }

      this.setPickerData(this.dateData.type as DatePickerType, this.dateData.dateComponents);
    }

    if (this.yearControl.value) {
      this.selectedYear = +this.yearControl.value;
    }
    if (this.monthControl.value) {
      this.selectedMonth = this.months.indexOf(this.monthControl.value);
    }
    if (this.dayControl.value) {
      this.selectedDay = +this.dayControl.value;
    }
  }

  private filterValues<T>(value: string | number, options: T[]): T[] {
    const filterValue = value.toString().toLowerCase();
    return options.filter((option) => option?.toString().toLowerCase().includes(filterValue));
  }

  private updateDays(): void {
    if (this.monthControl.value != null) {
      const monthIndex = this.months.indexOf(this.monthControl.value);

      const year = this.yearControl.value;

      if (monthIndex >= 0 && year) {
        const daysInMonth = new Date(+year, monthIndex, 0).getDate();
        this.days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
      } else {
        this.days = [];
      }
    }
  }

  setPickerData(type: DatePickerType | null, dateComponents: IDatePicker): void {
    this.pickerData = {
      type,
      dateComponents,
    };
  }

  blockTyping(event: KeyboardEvent) {
    event.preventDefault();
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent, type: DatePickerType) {
    if (type === DatePickerType.YEAR) {
      this.selectedYear = event.option.value;

      if (!this.selectedYear) {
        return;
      }

      this.selectedDay = undefined;
      this.dayControl.setValue(null);

      this.selectedMonth = undefined;
      this.monthControl.setValue(null);

      this.setPickerData(DatePickerType.YEAR, { year: this.selectedYear, month: undefined, day: undefined });
    }

    if (this.selectedYear) {
      if (type === DatePickerType.MONTH) {
        this.selectedDay = undefined;
        this.selectedMonth = undefined;
        this.dayControl.setValue(null);

        if (~this.months.indexOf(event.option.value)) {
          this.selectedMonth = this.months.indexOf(event.option.value);
        }

        if (this.selectedMonth) {
          this.setPickerData(DatePickerType.MONTH, {
            year: this.selectedYear,
            month: this.selectedMonth,
            day: undefined,
          });
        }
      }

      if (this.selectedMonth) {
        if (type === DatePickerType.DAY) {
          this.selectedDay = event.option.value;

          if (this.selectedDay) {
            this.setPickerData(DatePickerType.DAY, {
              year: this.selectedYear,
              month: this.selectedMonth,
              day: this.selectedDay,
            });
          }
        }
      }
    }

    this.dateChange.emit(this.pickerData);
  }


  protected readonly DatePickerType = DatePickerType;
}
